import { AnimationBuilder, AnimationPlayer } from "@angular/animations";
import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import {
	BasePortalOutlet,
	CdkPortalOutlet,
	ComponentPortal,
	TemplatePortal,
} from "@angular/cdk/portal";
import {
	Component,
	ComponentRef,
	ElementRef,
	EmbeddedViewRef,
	EventEmitter,
	Inject,
	OnInit,
	Optional,
	ViewChild,
	ViewEncapsulation,
	DOCUMENT,
} from "@angular/core";

import { DialogConfig } from "./dialog-config";

@Component({
	selector: "app-dialog",
	styles: ["app-dialog { width: 100% }"],
	template: "<ng-template cdkPortalOutlet></ng-template>",
	encapsulation: ViewEncapsulation.None,
	imports: [CdkPortalOutlet],
})
export class DialogContainerComponent extends BasePortalOutlet implements OnInit {
	@ViewChild(CdkPortalOutlet, { static: true }) portalOutlet: CdkPortalOutlet;

	animationStateChanged = new EventEmitter<string>();
	state: "visible" | "exit" = "visible";
	player: AnimationPlayer;
	private focusTrap: ConfigurableFocusTrap;

	constructor(
		private animationBuilder: AnimationBuilder,
		private elementRef: ElementRef,
		private focusTrapFactory: ConfigurableFocusTrapFactory,
		public config: DialogConfig,
		@Optional() @Inject(DOCUMENT) private document: any,
	) {
		super();

		if (config.animations) {
			const factory = this.animationBuilder.build(config.animations.open);

			this.player = factory.create(this.elementRef.nativeElement);
		}
	}

	ngOnInit(): void {
		this.player.onDone(() => {
			this.player.destroy();
			this.state = "visible";
			this.animationStateChanged.next(this.state);
		});
		this.player.play();
	}

	startExitAnimation(): void {
		if (this.config.animations) {
			this.state = "exit";
			const factory = this.animationBuilder.build(this.config.animations.close);
			this.player = factory.create(this.elementRef.nativeElement);
			this.player.onDone(() => {
				this.animationStateChanged.emit(this.state);
				this.player.destroy();
			});
			this.player.play();
		}
	}

	attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
		if (this.portalOutlet.hasAttached()) {
			throwMatDialogContentAlreadyAttachedError();
		}
		this.trapFocus();
		return this.portalOutlet.attachComponentPortal(portal);
	}

	attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
		if (this.portalOutlet.hasAttached()) {
			throwMatDialogContentAlreadyAttachedError();
		}
		this.trapFocus();
		return this.portalOutlet.attachTemplatePortal(portal);
	}

	private trapFocus(): void {
		const element = this.elementRef.nativeElement;
		if (!this.focusTrap) {
			this.focusTrap = this.focusTrapFactory.create(element);
		}

		if (this.config.autoFocus) {
			this.focusTrap.focusInitialElementWhenReady();
		} else {
			const activeElement = this.document.activeElement;
			if (activeElement !== element && !element.contains(activeElement)) {
				element.focus();
			}
		}
	}
}

export const throwMatDialogContentAlreadyAttachedError = () => {
	throw Error("Attempting to attach dialog content after content is already attached");
};
